import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, paginate, type PaginatorProps } from '@jeraisonne/ui';

/**
 * Pagination client d'une liste déjà chargée. Revient en page 1 quand une des
 * `resetKeys` change (recherche, filtre) et remonte en haut de la page à
 * chaque changement de page. À brancher sur `<Paginator {...paginator} />`.
 */
export function usePagedList<T>(
  items: readonly T[] | undefined,
  opts: { pageSize?: number; resetKeys?: readonly unknown[]; unit?: string; label?: string } = {},
) {
  const { pageSize = DEFAULT_PAGE_SIZE, resetKeys = [], unit = 'éléments', label } = opts;
  const [page, setPage] = useState(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), resetKeys);
  const slice = useMemo(() => paginate(items ?? [], page, pageSize), [items, page, pageSize]);
  const paginator: PaginatorProps = {
    page: slice.page,
    pageCount: slice.pageCount,
    onPageChange: (p: number) => {
      setPage(p);
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          /* jsdom */
        }
      }
    },
    summary: slice.total ? `${slice.from}–${slice.to} sur ${slice.total} ${unit}` : undefined,
    label,
  };
  return { items: slice.items, paginator, slice };
}
